
import React, { useEffect, useState } from 'react';
import { Package } from 'lucide-react';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { Link } from 'react-router-dom';

import { useAuth } from '../../context/AuthContext';
import { db } from '../../utils/firebase';
import { Navigate } from 'react-router-dom';

interface OrderDisplay {
  image: string;
  status: string;
  date: string;
  items: number;
  total: number;
}

const OrdersView: React.FC = () => {
  const { user, loading } = useAuth();
  const [orders, setOrders] = useState<OrderDisplay[]>([]);
  const [dataLoading, setDataLoading] = useState(true);

  useEffect(() => {
    if (!user) return;

    const q = query(
      collection(db, 'orders'),
      where('userId', '==', user.uid),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const fetchedOrders: OrderDisplay[] = snapshot.docs.map(doc => {
        const data = doc.data();
        const firstItem = data.items?.[0] || {};
        const date = data.createdAt?.toDate?.() || new Date(); // Handle timestamp or fallback
        
        return {
          id: doc.id,
          // Use first item's image or placeholder
          image: firstItem.image || '/assets/placeholder.svg',
          status: data.status || 'unknown',
          date: date.toLocaleDateString(),
          items: data.items?.length || 0,
          total: data.totalAmount || 0
        };
      });
      setOrders(fetchedOrders);
      setDataLoading(false);
    }, (error) => {
      console.error("Error fetching orders:", error);
      setDataLoading(false);
    });

    return () => unsubscribe();
  }, [user]);

  if (loading || dataLoading) return <div className="min-h-[50vh] flex items-center justify-center">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="max-w-2xl mx-auto py-8 px-4">
      <h2 className="font-heading text-xl tracking-widest mb-8 border-b border-gray-100 pb-4">YOUR ORDERS</h2>
      <div className="space-y-6">
        {orders.map(order => (
          <Link to={`/profile/orders/${order.id}`} key={order.id} className="block group">
            <div className="flex gap-4 p-4 border border-gray-100 rounded-sm group-hover:border-gray-300 transition-colors">
              <div className="w-20 h-20 bg-gray-100 flex-shrink-0">
                 <img src={order.image} alt="Order Item" className="w-full h-full object-cover" />
              </div>
              <div className="flex-1">
                 <div className="flex justify-between mb-2">
                   <span className="font-heading font-bold text-sm tracking-wide">#{order.id}</span>
                   <span className={`font-mono text-xs px-2 py-0.5 rounded-full ${
                      order.status === 'paid' ? 'text-green-600 bg-green-50' : 'text-yellow-600 bg-yellow-50'
                   }`}>
                     {order.status.toUpperCase()}
                   </span>
                 </div>
                 <p className="text-xs text-gray-500 font-body mb-2">Ordered on {order.date}</p>
                 <div className="flex justify-between items-end mt-4">
                   <span className="text-xs text-gray-400">{order.items} Item(s)</span>
                   <span className="font-heading font-bold text-sm bg-gray-900 text-white px-3 py-1 group-hover:bg-black transition-colors">£{order.total.toFixed(2)}</span>
                 </div>
              </div>
            </div>
          </Link>
        ))}
        {orders.length === 0 && (
          <div className="text-center py-12 text-gray-400">
            <Package size={48} className="mx-auto mb-4 opacity-50" />
            <p className="font-body text-sm">No orders found.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrdersView;
